import Image from "next/image";
import Link from "next/link";
import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import useStore from "@/store/useStore";
import SpaceCountdown, { useSpaceCountdownState } from "./SpaceCountdown";
import { usePreviewMode } from "@/components/providers/PreviewModeProvider";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPen,
  faEye,
  faArrowRight,
  faShare,
} from "@fortawesome/free-solid-svg-icons";
import { useResponsiveLayout, BREAKPOINTS } from "@/hooks/useResponsiveLayout";
import { supabase } from "@/utils/supabase/client";
import { getCachedOwnership } from "@/utils/supabase/cache";
import { motion, AnimatePresence } from "framer-motion";
import ShareModal from "./ShareModal";
import { isAdmin } from "@/utils/authHelpers";

/**
 * Space header component with logo and navigation
 * @param {Object} props - Component props
 * @param {boolean} props.isLightBackground - Whether the background is light
 * @param {Object} props.headerButtonStyles - Styles for header buttons
 * @param {Object} props.spaceData - Space data with expiration info
 * @param {boolean} props.isEditMode - Whether the space is in edit mode
 * @param {Function} props.toggleEditMode - Function to toggle edit mode
 * @param {number} props.unpublishedChangesCount - Number of unpublished changes (optional)
 * @param {Function} props.onOpenPublishTray - Function to open the publish tray (optional)
 * @param {Function} props.onChangeSlug - Function to open the change slug modal (optional)
 * @param {boolean} props.isOwnerOverride - Override ownership determination from parent (optional)
 */
const SpaceHeader = React.memo(function SpaceHeader({
  isLightBackground,
  spaceData,
  isEditMode,
  toggleEditMode,
  unpublishedChangesCount: propUnpublishedChangesCount,
  onOpenPublishTray,
  onChangeSlug,
  isOwnerOverride,
}) {
  const [isOwner, setIsOwner] = useState(false);
  const [isCheckingOwnership, setIsCheckingOwnership] = useState(true);
  const [unpublishedChanges, setUnpublishedChanges] = useState(0);
  const [prevUnpublishedChanges, setPrevUnpublishedChanges] = useState(0);
  const [isNumberAnimating, setIsNumberAnimating] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);

  const [isChipHovered, setIsChipHovered] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isUserAdmin, setIsUserAdmin] = useState(false);
  const session = useStore((state) => state.session);
  const showToast = useStore((state) => state.showToast);
  const { previewMode } = usePreviewMode();
  const { windowWidth } = useResponsiveLayout();
  const isMobileDevice = windowWidth <= BREAKPOINTS.mobile;

  // Refs to prevent duplicate calls
  const ownershipCheckRef = useRef(false);
  const unpublishedChangesRef = useRef(false);

  // Use state to track the light background status
  const [useLight, setUseLight] = useState(isLightBackground);

  // Check if we should show countdown instead of unpublished changes
  const { shouldShowCountdown, hasConflict, expirationAt } =
    useSpaceCountdownState(spaceData);

  // Use prop value if provided, otherwise use local state
  const displayedUnpublishedChanges =
    propUnpublishedChangesCount !== undefined
      ? propUnpublishedChangesCount
      : unpublishedChanges;

  // Track initialization
  useEffect(() => {
    if (!hasInitialized && displayedUnpublishedChanges !== undefined) {
      setHasInitialized(true);
      setPrevUnpublishedChanges(displayedUnpublishedChanges);
    }
  }, [hasInitialized, displayedUnpublishedChanges]);

  // Track when the number changes to trigger animation
  useEffect(() => {
    if (
      hasInitialized &&
      displayedUnpublishedChanges !== prevUnpublishedChanges
    ) {
      // Normal number change animation
      setIsNumberAnimating(true);
      setPrevUnpublishedChanges(displayedUnpublishedChanges);

      // Reset animation state after animation completes
      const timer = setTimeout(() => {
        setIsNumberAnimating(false);
      }, 600);

      return () => clearTimeout(timer);
    }
  }, [displayedUnpublishedChanges, prevUnpublishedChanges, hasInitialized]);

  // Handler for when the unpublished changes chip is clicked
  const handleChipClick = useCallback(() => {
    // Scroll to the QuickActions component at the bottom of the page
    const quickActionsElement = document.getElementById("quick-actions");
    if (quickActionsElement) {
      quickActionsElement.scrollIntoView({ behavior: "smooth" });

      // Add a brief highlight effect to the publish button
      const publishButton = document.querySelector(
        '[data-publish-button="true"]'
      );
      if (publishButton) {
        publishButton.classList.add("highlight-publish-button");
        setTimeout(() => {
          publishButton.classList.remove("highlight-publish-button");
        }, 2000);
      }
    }

    // Open the publish tray if the function is available
    if (typeof onOpenPublishTray === "function") {
      onOpenPublishTray();
    }
  }, [onOpenPublishTray]);

  // Handler for when countdown is clicked - triggers publish action
  const handleCountdownClick = useCallback(() => {
    // First, try to find and click the main publish button to open the tray
    const publishButton = document.querySelector(
      '[data-publish-button="true"]'
    );

    if (publishButton) {
      // Check if we're on mobile by looking at window width or checking if we're on a mobile device
      const isMobile = window.innerWidth <= 768; // BREAKPOINTS.mobile equivalent

      if (isMobile) {
        // On mobile, the publish button shows a confirmation dialog directly
        // So we can just click it directly
        publishButton.click();
      } else {
        // On desktop, we need to click the publish button first to open the tray
        publishButton.click();

        // Then after a short delay, click the actual publish button in the tray
        setTimeout(() => {
          const trayPublishButton = document.querySelector(
            '[data-publish-button="true"] + div button:last-child, .bg-primary.text-white[class*="rounded-lg"]'
          );

          if (
            trayPublishButton &&
            trayPublishButton.textContent?.includes("Publish")
          ) {
            console.log("Clicking tray publish button");
            trayPublishButton.click();
          } else {
            // If we can't find the tray button, look for it more broadly
            const allPublishButtons = document.querySelectorAll("button");
            for (const button of allPublishButtons) {
              if (
                button.textContent?.includes("Publish") &&
                !button.textContent?.includes("Published") &&
                button.classList.contains("bg-primary")
              ) {
                console.log("Found and clicking publish button in tray");
                button.click();
                break;
              }
            }
          }
        }, 100); // Small delay to ensure tray is opened
      }
    } else if (typeof onOpenPublishTray === "function") {
      // Fallback to opening publish tray
      onOpenPublishTray();
    }
  }, [onOpenPublishTray]);

  // Handler to open publish flow from share modal
  const handleOpenPublish = useCallback(() => {
    // Look for the publish button in the QuickActions component
    const publishButton = document.querySelector(
      '[data-publish-button="true"]'
    );

    if (publishButton) {
      // Trigger the publish button which will open the publish tray/modal
      publishButton.click();

      showToast({
        title: "Publish Required",
        description: "Please publish your space to enable sharing features.",
        variant: "default",
      });
    } else {
      // Fallback - show instructions to user
      showToast({
        title: "Publish Required",
        description:
          "You need to publish your space before sharing. Look for the Publish button in the editor.",
        variant: "default",
      });
    }
  }, [showToast]);

  // Handler to open share modal
  const handleOpenShareModal = useCallback(() => {
    // Only allow opening if user is owner and admin
    if (isOwner && !isCheckingOwnership && isUserAdmin) {
      setIsShareModalOpen(true);
    }
  }, [isOwner, isCheckingOwnership, isUserAdmin]);

  // Update the light background status when previewMode or isLightBackground changes
  useEffect(() => {
    setUseLight(previewMode ? true : isLightBackground);
  }, [previewMode, isLightBackground]);

  // Check if user is admin or super admin
  useEffect(() => {
    if (session) {
      const adminStatus = isAdmin(session, true); // Allow graceful fail
      setIsUserAdmin(adminStatus);
    } else {
      setIsUserAdmin(false);
    }
  }, [session]);

  // Memoized space ID to prevent unnecessary re-runs
  const spaceId = useMemo(
    () => spaceData?.space_data?.id,
    [spaceData?.space_data?.id]
  );

  // Optimized unpublished changes fetch with silent failures
  useEffect(() => {
    // Only fetch if we're the owner, in edit mode, and have a space ID
    if (!isOwner || !isEditMode || !spaceId) return;

    // Prevent duplicate calls
    if (unpublishedChangesRef.current) return;

    const fetchUnpublishedChanges = async () => {
      unpublishedChangesRef.current = true;

      try {
        // Use a longer timeout for this non-critical operation and make it completely silent
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 20000); // 20 second timeout

        const { data, error } = await supabase
          .from("space_drafts")
          .select("unpublished_changes_count")
          .eq("id", spaceId)
          .single()
          .abortSignal(controller.signal);

        clearTimeout(timeoutId);

        if (error) {
          // Silent failure - this is non-critical
          return;
        }

        setUnpublishedChanges(data?.unpublished_changes_count || 0);
      } catch (err) {
        // Completely silent failure for this non-critical operation
        // This prevents console spam when network is slow or database is busy
      } finally {
        // Reset ref after a delay
        setTimeout(() => {
          unpublishedChangesRef.current = false;
        }, 1000);
      }
    };

    fetchUnpublishedChanges();

    // Reduced polling frequency to every 30 seconds to reduce load
    const intervalId = setInterval(() => {
      if (!unpublishedChangesRef.current) {
        fetchUnpublishedChanges();
      }
    }, 30000);

    return () => {
      clearInterval(intervalId);
    };
  }, [isOwner, isEditMode, spaceId]);

  // Memoized space and user identifiers to prevent unnecessary re-runs
  const spaceIdentifier = useMemo(
    () => ({
      slug: spaceData?.space_data?.slug,
      ownerId: spaceData?.space_data?.owner_id,
      userId: spaceData?.space_data?.user_id,
      isOwner: spaceData?.space_data?.is_owner,
    }),
    [
      spaceData?.space_data?.slug,
      spaceData?.space_data?.owner_id,
      spaceData?.space_data?.user_id,
      spaceData?.space_data?.is_owner,
    ]
  );

  const userIdentifier = useMemo(() => session?.user?.id, [session?.user?.id]);

  // Optimized ownership check with caching and deduplication
  useEffect(() => {
    // If we have an explicit override from parent, use it
    if (isOwnerOverride !== undefined) {
      console.log("📄 Using ownership override from parent:", isOwnerOverride);
      setIsOwner(isOwnerOverride);
      setIsCheckingOwnership(false);
      return;
    }

    // Prevent duplicate calls
    if (ownershipCheckRef.current) return;

    const checkOwnership = async () => {
      if (!userIdentifier || !spaceIdentifier.slug) {
        setIsOwner(false);
        setIsCheckingOwnership(false);
        return;
      }

      // Additional safety check - if no session, definitely not owner
      if (!session?.user?.id) {
        console.log("📄 No authenticated session, not owner");
        setIsOwner(false);
        setIsCheckingOwnership(false);
        return;
      }

      // Mark as checking to prevent duplicate calls
      ownershipCheckRef.current = true;

      try {
        // Immediate fallback check using available data
        const fallbackIsOwner =
          spaceIdentifier.isOwner === true ||
          spaceIdentifier.ownerId === userIdentifier ||
          spaceIdentifier.userId === userIdentifier;

        // Check if we have explicit ownership data (is_owner field is set)
        const hasExplicitOwnership = spaceIdentifier.isOwner !== undefined;

        // If we have explicit ownership data, trust it completely
        if (hasExplicitOwnership) {
          console.log(
            "📄 Using explicit ownership data:",
            spaceIdentifier.isOwner
          );
          setIsOwner(spaceIdentifier.isOwner === true);
          setIsCheckingOwnership(false);
          return;
        }

        // If we have owner/user ID data but no explicit ownership flag, verify via RPC
        // This happens when using public endpoint but user is authenticated
        const hasIdData = spaceIdentifier.ownerId || spaceIdentifier.userId;

        if (hasIdData) {
          // We have some ID data, but need to verify ownership via RPC for safety
          console.log(
            "📄 Have ID data but no explicit ownership, checking via RPC"
          );
        }

        // Use cached ownership check to prevent duplicate API calls
        const ownershipResult = await getCachedOwnership(
          supabase,
          spaceIdentifier.slug,
          userIdentifier
        );

        if (ownershipResult !== null) {
          setIsOwner(ownershipResult);
        } else {
          // Fallback if API call failed - be more conservative
          console.log("⚠️ RPC failed, defaulting to NOT owner for safety");
          setIsOwner(false);
        }
      } catch (err) {
        console.warn("⚠️ Ownership check error:", err);
        setIsOwner(false);
      } finally {
        setIsCheckingOwnership(false);
        // Reset the ref after a short delay to allow for re-checks if needed
        setTimeout(() => {
          ownershipCheckRef.current = false;
        }, 1000);
      }
    };

    checkOwnership();
  }, [userIdentifier, spaceIdentifier, isOwnerOverride]);

  // Animation variants for the chip
  const chipVariants = {
    hidden: {
      opacity: 0,
      scale: 0.8,
      y: -10,
    },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 20,
        duration: 0.4,
      },
    },
    exit: {
      opacity: 0,
      scale: 0.8,
      y: -10,
      transition: {
        duration: 0.3,
      },
    },
  };

  // Animation variants for the number badge
  const numberBadgeVariants = {
    initial: { scale: 1 },
    animate: {
      scale: [1, 1.3, 1],
      transition: {
        duration: 0.6,
        ease: [0.68, -0.55, 0.265, 1.55],
      },
    },
  };

  // Animation variants for the number text
  const numberTextVariants = {
    initial: { y: 0, opacity: 1 },
    exit: {
      y: -10,
      opacity: 0,
      transition: { duration: 0.2 },
    },
    enter: {
      y: [10, 0],
      opacity: [0, 1],
      transition: { duration: 0.2 },
    },
  };

  if (!spaceData?.space_data) return null;

  return (
    <header className="w-full max-w-7xl mx-auto py-4 bg-opacity-90 flex-shrink-0 relative z-10">
      {/* Add global style for the publish button highlight effect */}
      <style jsx global>{`
        @keyframes highlightPulse {
          0% {
            box-shadow: 0 0 0 0 rgba(79, 70, 229, 0.7);
          }
          70% {
            box-shadow: 0 0 0 15px rgba(79, 70, 229, 0);
          }
          100% {
            box-shadow: 0 0 0 0 rgba(79, 70, 229, 0);
          }
        }

        .highlight-publish-button {
          animation: highlightPulse 1s ease-out infinite;
          transform: scale(1.05);
        }
      `}</style>

      <div className="flex justify-between items-center px-4">
        <Link href="/">
          <Image
            src="/logos/beta/white.svg"
            alt="Logo"
            width={120}
            height={40}
            className={useLight ? "brightness-0" : ""}
            priority
          />
        </Link>

        {/* Countdown or Unpublished changes chip - hidden on mobile */}
        <AnimatePresence mode="wait">
          {!isMobileDevice &&
            isOwner &&
            isEditMode &&
            ((shouldShowCountdown && expirationAt) ||
              displayedUnpublishedChanges > 0) && (
              <motion.div
                className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10"
                variants={chipVariants}
                initial={hasInitialized ? "hidden" : false}
                animate="visible"
                exit="exit"
                style={{ x: "-50%", y: "-50%" }}
              >
                {shouldShowCountdown && expirationAt ? (
                  // Show countdown for first-time drafts
                  <SpaceCountdown
                    expirationAt={expirationAt}
                    hasConflict={hasConflict}
                    spaceSlug={spaceData?.space_data?.slug}
                    onPublishClick={handleCountdownClick}
                    onChangeSlug={onChangeSlug}
                  />
                ) : (
                  // Show unpublished changes chip
                  <motion.div
                    className={`
                    px-3 py-1.5 rounded-full text-sm font-medium flex items-center
                    shadow-md transition-all duration-300 hover:scale-105 cursor-pointer
                    ${
                      displayedUnpublishedChanges > 10
                        ? "animate-pulse"
                        : "hover:animate-pulse"
                    }
                    ${
                      useLight
                        ? "bg-dark text-white border border-dark/80 hover:bg-primary-dark"
                        : "bg-white text-primary border border-white/80 hover:bg-gray-100"
                    }
                  `}
                    onClick={handleChipClick}
                    title="Click to publish your changes"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onMouseEnter={() => setIsChipHovered(true)}
                    onMouseLeave={() => setIsChipHovered(false)}
                  >
                    <motion.span
                      className={`
                      inline-flex items-center justify-center rounded-full 
                      w-5 h-5 mr-2 text-xs font-bold transition-all duration-300 overflow-hidden
                      ${useLight ? "bg-accent-lime text-dark" : "bg-primary text-white"}
                    `}
                      variants={numberBadgeVariants}
                      initial="initial"
                      animate={
                        hasInitialized && isNumberAnimating
                          ? "animate"
                          : "initial"
                      }
                    >
                      <AnimatePresence mode="wait">
                        <motion.span
                          key={displayedUnpublishedChanges}
                          variants={numberTextVariants}
                          initial={hasInitialized ? "exit" : false}
                          animate="enter"
                          exit="exit"
                        >
                          {displayedUnpublishedChanges > 99
                            ? "99+"
                            : displayedUnpublishedChanges}
                        </motion.span>
                      </AnimatePresence>
                    </motion.span>
                    <span>
                      {displayedUnpublishedChanges === 1
                        ? "change not published"
                        : "changes not published"}
                    </span>

                    {/* Arrow icon that appears on hover */}
                    <motion.span
                      initial={{ opacity: 0, x: -5 }}
                      animate={{
                        opacity: isChipHovered ? 1 : 0,
                        x: isChipHovered ? 0 : -5,
                      }}
                      transition={{ duration: 0.2 }}
                      className="ml-2 text-primary"
                    >
                      <FontAwesomeIcon icon={faArrowRight} size="sm" />
                    </motion.span>
                  </motion.div>
                )}
              </motion.div>
            )}
        </AnimatePresence>

        <div className="flex items-center gap-4">
          {/* Share Button - only visible for space owner who is admin/super admin */}
          {isOwner && !isCheckingOwnership && isUserAdmin && (
            <motion.button
              onClick={handleOpenShareModal}
              className={`
                flex items-center justify-center transition-colors
                ${
                  isMobileDevice
                    ? "w-10 h-10 rounded-full"
                    : "px-3 py-2 rounded-md"
                }
                ${
                  useLight
                    ? "bg-gray-800/10 hover:bg-gray-800/20 text-gray-800"
                    : "bg-white/20 hover:bg-white/30 text-white"
                }
              `}
              aria-label="Share space"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <FontAwesomeIcon icon={faShare} className="w-4 h-4" />
              {!isMobileDevice && <span className="ml-2">Share</span>}
            </motion.button>
          )}

          {/* Edit Mode Toggle Button - only visible for space owner */}
          {isOwner && !isCheckingOwnership && toggleEditMode && (
            <motion.button
              onClick={toggleEditMode}
              className={`
                flex items-center justify-center transition-colors
                ${
                  isMobileDevice
                    ? "w-10 h-10 rounded-full"
                    : "px-3 py-2 rounded-md"
                }
                ${
                  useLight
                    ? "bg-gray-800/10 hover:bg-gray-800/20 text-gray-800"
                    : "bg-white/20 hover:bg-white/30 text-white"
                }
              `}
              aria-label={isEditMode ? "View mode" : "Edit mode"}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <FontAwesomeIcon
                icon={isEditMode ? faEye : faPen}
                className="w-4 h-4"
              />
              {!isMobileDevice && (
                <span className="ml-2">
                  {isEditMode ? "Stop Editing" : "Edit"}
                </span>
              )}
            </motion.button>
          )}

          {session?.user ? (
            <Link href="/user/spaces">
              <motion.div
                className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${
                  useLight
                    ? "bg-gray-800/10 hover:bg-gray-800/20"
                    : "bg-white/20 hover:bg-white/30"
                }`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  className={
                    useLight ? "w-6 h-6 text-gray-800" : "w-6 h-6 text-white"
                  }
                >
                  <path d="M11.47 3.84a.75.75 0 011.06 0l8.69 8.69a.75.75 0 101.06-1.06l-8.689-8.69a2.25 2.25 0 00-3.182 0l-8.69 8.69a.75.75 0 001.061 1.06l8.69-8.69z" />
                  <path d="M12 5.432l8.159 8.159c.03.03.06.058.091.086v6.198c0 1.035-.84 1.875-1.875 1.875H15a.75.75 0 01-.75-.75v-4.5a.75.75 0 00-.75-.75h-3a.75.75 0 00-.75.75V21a.75.75 0 01-.75.75H5.625a1.875 1.875 0 01-1.875-1.875v-6.198a2.29 2.29 0 00.091-.086L12 5.43z" />
                </svg>
              </motion.div>
            </Link>
          ) : (
            <Link
              href="/onboarding"
              className={`
                rounded-lg px-4 py-2 text-sm transition-colors font-bold
                ${useLight ? "bg-black text-white" : "bg-white text-black"}
              `}
            >
              Create your Qensa
            </Link>
          )}
        </div>
      </div>

      {/* Share Modal */}
      <ShareModal
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        spaceData={spaceData}
        onPublish={handleOpenPublish}
      />
    </header>
  );
});

export default SpaceHeader;
